import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { fmtDate } from "@/lib/credits";
import { ShieldCheck, ShieldX, Loader2, Search, Ban } from "lucide-react";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.05] py-3 last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</span>
      <span className="text-right text-sm font-medium text-white">{value || "—"}</span>
    </div>
  );
}

export default function Verify() {
  const { certNumber } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(!!certNumber);
  const [query, setQuery] = useState(certNumber || "");

  const lookup = useCallback(async (cert) => {
    if (!cert) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(cert.trim())}`);
      setResult(data);
    } catch (e) {
      setResult({ valid: false });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (certNumber) lookup(certNumber); }, [certNumber, lookup]);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/verify/${encodeURIComponent(query.trim().toUpperCase())}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="verify-page">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-5 py-10">
        <div className="mb-10 flex justify-center">
          <div className="h-9"><Logo className="h-9" showText /></div>
        </div>

        <form onSubmit={onSearch} className="mb-8 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter certificate number (e.g. SGC-2026-000001)"
              className="h-12 w-full rounded-xl border border-white/[0.1] bg-[#141414] pl-10 pr-4 text-sm text-white placeholder:text-neutral-600 focus:border-[#E30613] focus:outline-none focus:ring-1 focus:ring-[#E30613]"
              data-testid="verify-input"
            />
          </div>
          <button type="submit" className="rounded-xl bg-[#E30613] px-6 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A]" data-testid="verify-submit">Verify</button>
        </form>

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !result ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center text-neutral-500">
            <ShieldCheck className="mb-4 h-10 w-10 text-neutral-700" />
            <p className="text-sm">Enter a certificate number above to verify its authenticity.</p>
          </div>
        ) : !result.valid && !result.voided ? (
          <div className="rounded-2xl border border-[#E30613]/30 bg-[#E30613]/[0.06] p-8 text-center" data-testid="verify-invalid">
            <ShieldX className="mx-auto mb-4 h-12 w-12 text-[#FF6069]" />
            <h1 className="font-display text-2xl font-semibold text-white">Invalid Certificate</h1>
            <p className="mt-2 text-sm text-neutral-400">We could not find a SGBINOXE installation certificate matching this number.</p>
          </div>
        ) : (
          <div data-testid="verify-result">
            {result.voided ? (
              <div className="mb-6 flex items-center gap-4 rounded-2xl border border-[#E30613]/30 bg-[#E30613]/[0.06] p-6" data-testid="verify-voided">
                <Ban className="h-10 w-10 shrink-0 text-[#FF6069]" />
                <div>
                  <h1 className="font-display text-xl font-semibold text-white">Certificate Voided</h1>
                  <p className="mt-1 text-sm text-neutral-400">This installation certificate has been voided.</p>
                </div>
              </div>
            ) : (
              <div className="mb-6 flex items-center gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6" data-testid="verify-valid">
                <ShieldCheck className="h-10 w-10 shrink-0 text-emerald-400" />
                <div>
                  <h1 className="font-display text-xl font-semibold text-white">Valid Installation Certificate</h1>
                  <p className="mt-1 text-sm text-neutral-400">This is an authentic SGBINOXE installation certificate.</p>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6">
              <Row label="Certificate Number" value={result.certificate_number} />
              <Row label="Status" value={<span className="capitalize">{result.certificate_status}</span>} />
              <Row label="Customer" value={result.customer_name} />
              <Row label="Vehicle" value={`${result.vehicle_year} ${result.vehicle_make} ${result.vehicle_model}`} />
              <Row label="VIN (last 6)" value={`••••••••••• ${result.vin_last6}`} />
              <Row label="Installation Date" value={fmtDate(result.installation_date)} />
              <Row label="Installer" value={result.installer_business} />
              <Row label="Product Installed" value={result.product_installed} />
            </div>
          </div>
        )}

        <p className="mt-auto pt-10 text-center text-xs text-neutral-600">
          Protected by SGBINOXE Care · Certificate verification is provided for authenticity only and does not constitute a warranty.
        </p>
      </div>
    </div>
  );
}
